import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { trackEvent, trackPageView } from '@/shared/lib/analytics';
import { ParallaxSection } from '@/features/landing/components/ParallaxSection';
import { HeroSection } from '@/features/landing/sections/HeroSection';
import { TemplateShowcaseSection } from '@/features/landing/sections/TemplateShowcaseSection';
import { FeatureCardsSection } from '@/features/landing/sections/FeatureCardsSection';
import { ProSection } from '@/features/landing/sections/ProSection';
import { FooterSection } from '@/features/landing/sections/FooterSection';
import { PageLayout } from '@/shared/layouts/PageLayout';
import { AuthProvider } from '@/features/auth/providers/AuthProvider';
import { SubscriptionProvider } from '@/features/subscription';
import { PublicRoute } from '@/features/auth/guards/PublicRoute';
import { ProtectedRoute } from '@/features/auth/guards/ProtectedRoute';
import { Toaster } from '@/shared/components/ui/sonner';
import { CookieConsent } from '@/shared/components/CookieConsent';
import { UserAuthButton } from '@/features/auth/components/UserAuthButton';
import { FloatingShapes } from '@/shared/components/FloatingShapes';
import { ErrorBoundary } from '@/shared/components/ErrorBoundary';
import { OfflineBanner } from '@/shared/components/OfflineBanner';
import { Loader2 } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'));
const TemplatesPage = lazy(() => import('@/features/templates/pages/TemplatesPage'));

const SignInPage = lazy(() => import('@/features/auth/pages/SignInPage'));
const SignUpPage = lazy(() => import('@/features/auth/pages/SignUpPage'));
const ForgotPasswordPage = lazy(() => import('@/features/auth/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/features/auth/pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('@/features/auth/pages/VerifyEmailPage'));
const AuthCallbackPage = lazy(() => import('@/features/auth/pages/AuthCallbackPage'));
const AccountPage = lazy(() => import('@/features/account/pages/AccountPage'));
const SettingsPage = lazy(() => import('@/features/settings/pages/SettingsPage'));
const EditorPage = lazy(() => import('@/features/editor/pages/EditorPage'));
const PreviewPage = lazy(() => import('@/features/editor/pages/PreviewPage'));
const TemplateEditorPage = lazy(() => import('@/features/editor/pages/TemplateEditorPage'));
const DevVisualCompare = lazy(() => import('@/features/editor/pages/DevVisualCompare'));
const PrivacyPolicyPage = lazy(() => import('@/features/landing/pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('@/features/landing/pages/TermsOfServicePage'));
const CookiePolicyPage = lazy(() => import('@/features/landing/pages/CookiePolicyPage'));
const NotFoundPage = lazy(() => import('@/shared/pages/NotFoundPage'));

function AuthFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <Loader2 className="size-8 animate-spin text-primary" />
    </div>
  );
}

const LANDING_JSON_LD = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebApplication',
      name: 'Safhati',
      url: 'https://safhati.io',
      description: 'Create beautiful single-page websites in minutes — no code needed.',
      applicationCategory: 'DesignApplication',
      operatingSystem: 'All',
      browserRequirements: 'Requires JavaScript',
      inLanguage: ['en', 'ar'],
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'USD',
        lowPrice: '0',
        highPrice: '15',
        offerCount: '2',
      },
    },
    {
      '@type': 'Organization',
      name: 'Safhati',
      url: 'https://safhati.io',
      logo: 'https://safhati.io/img/logo-white-trimmed.png',
    },
  ],
});

function LandingPage() {
  useEffect(() => {
    trackEvent('landing_page_viewed');
  }, []);

  return (
    <PageLayout>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: LANDING_JSON_LD }}
      />
      <FloatingShapes />
      <UserAuthButton />

      <HeroSection />
      <TemplateShowcaseSection />

      <ParallaxSection
        imageUrl="https://images.unsplash.com/photo-1483728642387-6c3bdd6c93e5?w=1920&q=80"
        translationKey="parallax.creativity"
        height="350px"
      />

      <FeatureCardsSection />

      <ParallaxSection
        imageUrl="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80"
        translationKey="parallax.beautiful"
      />

      <ProSection />

      <FooterSection />
    </PageLayout>
  );
}

export default function App() {
  const location = useLocation();

  useEffect(() => {
    trackPageView();
  }, [location.pathname]);

  return (
    <ErrorBoundary>
    <AuthProvider>
      <SubscriptionProvider>
        <OfflineBanner />
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            {/* Public landing page */}
            <Route path="/" element={<LandingPage />} />

            {/* Legal pages */}
            <Route
              path="/privacy"
              element={<Suspense fallback={<AuthFallback />}><PrivacyPolicyPage /></Suspense>}
            />
            <Route
              path="/terms"
              element={<Suspense fallback={<AuthFallback />}><TermsOfServicePage /></Suspense>}
            />
            <Route
              path="/cookies"
              element={<Suspense fallback={<AuthFallback />}><CookiePolicyPage /></Suspense>}
            />

            {/* Auth routes - redirect to /app if already signed in */}
            <Route element={<PublicRoute />}>
              <Route
                path="/auth/signin"
                element={<ErrorBoundary><Suspense fallback={<AuthFallback />}><SignInPage /></Suspense></ErrorBoundary>}
              />
              <Route
                path="/auth/signup"
                element={<ErrorBoundary><Suspense fallback={<AuthFallback />}><SignUpPage /></Suspense></ErrorBoundary>}
              />
              <Route
                path="/auth/forgot-password"
                element={<ErrorBoundary><Suspense fallback={<AuthFallback />}><ForgotPasswordPage /></Suspense></ErrorBoundary>}
              />
              <Route
                path="/auth/verify-email"
                element={<ErrorBoundary><Suspense fallback={<AuthFallback />}><VerifyEmailPage /></Suspense></ErrorBoundary>}
              />
            </Route>

            {/* Auth callback - no guard, processes tokens */}
            <Route
              path="/auth/callback"
              element={<ErrorBoundary><Suspense fallback={<AuthFallback />}><AuthCallbackPage /></Suspense></ErrorBoundary>}
            />

            {/* Reset password - no guard, needs recovery session */}
            <Route
              path="/auth/reset-password"
              element={<ErrorBoundary><Suspense fallback={<AuthFallback />}><ResetPasswordPage /></Suspense></ErrorBoundary>}
            />

            {/* Templates page - accessible to all users */}
            <Route
              path="/app/templates"
              element={<ErrorBoundary><Suspense fallback={<AuthFallback />}><TemplatesPage /></Suspense></ErrorBoundary>}
            />

            {/* Template preview editor - accessible to all users */}
            <Route
              path="/app/try/:templateId"
              element={<ErrorBoundary><Suspense fallback={<AuthFallback />}><TemplateEditorPage /></Suspense></ErrorBoundary>}
            />

            {/* Dev-only: visual comparison tool */}
            {import.meta.env.DEV && (
              <Route
                path="/dev/visual-compare"
                element={<Suspense fallback={<AuthFallback />}><DevVisualCompare /></Suspense>}
              />
            )}

            {/* Protected app routes */}
            <Route element={<ProtectedRoute />}>
              <Route
                path="/app"
                element={<ErrorBoundary><Suspense fallback={<AuthFallback />}><DashboardPage /></Suspense></ErrorBoundary>}
              />
              <Route
                path="/app/account"
                element={<ErrorBoundary><Suspense fallback={<AuthFallback />}><AccountPage /></Suspense></ErrorBoundary>}
              />
              <Route
                path="/app/settings"
                element={<ErrorBoundary><Suspense fallback={<AuthFallback />}><SettingsPage /></Suspense></ErrorBoundary>}
              />
              <Route
                path="/app/editor/:pageId"
                element={<ErrorBoundary><Suspense fallback={<AuthFallback />}><EditorPage /></Suspense></ErrorBoundary>}
              />
              <Route
                path="/app/preview/:pageId"
                element={<ErrorBoundary><Suspense fallback={<AuthFallback />}><PreviewPage /></Suspense></ErrorBoundary>}
              />
            </Route>

            {/* 404 catch-all */}
            <Route
              path="*"
              element={<Suspense fallback={<AuthFallback />}><NotFoundPage /></Suspense>}
            />
          </Routes>
        </AnimatePresence>
        <Toaster />
        <CookieConsent />
      </SubscriptionProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}
